import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../utils/adminApi";
import "./Book.css";

const PICKUP_OPTIONS = ["Pickup & Drop", "Self Drop"];

const SERVICE_OPTIONS = [
  "Screen Repair",
  "Battery Replacement",
  "Water Damage Repair",
  "Charging Issue",
  "Speaker / Mic Issue",
  "Software Issue",
  "Other",
];

const Book = () => {
  const { brand, model } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [customerToken, setCustomerToken] = useState("");
  const [address, setAddress] = useState("");

  const [pickupOption, setPickupOption] = useState("Pickup & Drop");
  const [issueOne, setIssueOne] = useState("");
  const [issueTwo, setIssueTwo] = useState("");

  const [otherIssueOne, setOtherIssueOne] = useState("");
  const [otherIssueTwo, setOtherIssueTwo] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ================= LIVE LOCATION ================= */

  const getLiveLocation = () => {
    if (!navigator.geolocation) {
      alert("Location not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );

          const data = await res.json();

          if (data && data.display_name) {
            setAddress(data.display_name);
          }
        } catch {
          setAddress(`https://www.google.com/maps?q=${lat},${lng}`);
        }
      },
      () => {
        alert("Unable to get location");
      }
    );
  };

  /* ================= CHECK LOGIN ================= */

  useEffect(() => {
    const raw = sessionStorage.getItem("bookingCustomer");
    const token = String(sessionStorage.getItem("bookingCustomerToken") || "").trim();

    if (!raw || !token) {
      sessionStorage.removeItem("bookingCustomer");
      sessionStorage.removeItem("bookingCustomerToken");
      navigate("/login", { replace: true });
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      if (!parsed?.name || !parsed?.phone || !parsed?.email) {
        navigate("/login", { replace: true });
        return;
      }

      setCustomer(parsed);
      setCustomerToken(token);
    } catch {
      sessionStorage.removeItem("bookingCustomer");
      sessionStorage.removeItem("bookingCustomerToken");
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || !customer) return;

    const decodedBrand = decodeURIComponent(brand || "");
    const decodedModel = decodeURIComponent(model || "");

    if (!issueOne) {
      alert("Please select Primary Issue.");
      return;
    }

    if (issueTwo && issueOne === issueTwo) {
      alert("Please choose two different repair issues.");
      return;
    }

    if (issueOne === "Other" && !otherIssueOne.trim()) {
      alert("Please describe the primary issue.");
      return;
    }

    if (issueTwo === "Other" && !otherIssueTwo.trim()) {
      alert("Please describe the secondary issue.");
      return;
    }

    if (pickupOption === "Pickup & Drop" && !address.trim()) {
      alert("Please enter pickup address.");
      return;
    }

    const toFinalIssue = (issue, otherText) => {
      if (!issue) return "";
      if (issue === "Other") return `Other: ${otherText.trim()}`;
      return issue;
    };

    const finalIssues = [
      toFinalIssue(issueOne, otherIssueOne),
      toFinalIssue(issueTwo, otherIssueTwo),
    ].filter(Boolean);

    const finalAddress =
      pickupOption === "Pickup & Drop"
        ? address.trim()
        : "Self Drop at Service Center";

    setIsSubmitting(true);

    try {
      const response = await adminApi.post(
        "/api/bookings",
        {
          brand: decodedBrand,
          model: decodedModel,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          pickupOption,
          address: finalAddress,
          pickupAddress:
            pickupOption === "Pickup & Drop" ? finalAddress : "",
          service: finalIssues.join(", "),
        },
        {
          // Booking API can take >45s on cold start; avoid false "Booking failed".
          timeout: 180000,
          headers: {
            "x-customer-auth": customerToken
          }
        }
      );

      const trackingId =
        response.data?.trackingId || response.data?._id;

      navigate(`/track/${trackingId}/${customer.phone}`, {
        state: {
          fromBooking: true,
          trackingId,
          phone: customer.phone,
          prefillBooking: {
            trackingId,
            brand: decodedBrand,
            model: decodedModel,
            service: finalIssues.join(", "),
            status: "Pending",
            pickupOption,
            address: finalAddress,
            technicianName: "",
            technicianPhone: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        },
      });

    } catch (error) {
      if (error.code === "ECONNABORTED") {
        alert("Server is taking longer than expected. Please wait and retry.");
        return;
      }

      if (error.response?.status === 401) {
        sessionStorage.removeItem("bookingCustomer");
        sessionStorage.removeItem("bookingCustomerToken");
        alert(error.response?.data?.error || "Session expired. Please login again.");
        navigate("/login", { replace: true });
        return;
      }

      const responseData = error.response?.data;
      const serverMessage =
        (typeof responseData === "string" && responseData) ||
        responseData?.error ||
        responseData?.message;

      alert(serverMessage || "Booking failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!customer) return null;

  return (
    <div className="book-wrapper">
      <h2 className="tech-title">Servizy  Repair</h2>

      <div className="glass-card main-info">
        <div className="device-header">
          <p><span>Brand:</span> {decodeURIComponent(brand || "")}</p>
          <p><span>Model:</span> {decodeURIComponent(model || "")}</p>
          <div className="status-badge-tech">SECURE BOOKING</div>
        </div>
      </div>

      <div className="glass-card customer-info">
        <div className="customer-header">
          <p><span>Name:</span> {customer.name}</p>
          <p><span>Phone:</span> {customer.phone}</p>
          <p><span>Email:</span> {customer.email}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="booking-form-surgical">

        {/* PICKUP TYPE */}

        <div className="input-group-surgical">
          <label className="tech-label">Pickup Type</label>

          <div className="pickup-options-grid">
            {PICKUP_OPTIONS.map((option) => (
              <label
                key={option}
                className={`tech-chip ${
                  pickupOption === option ? "active" : ""
                }`}
              >
                <input
                  type="radio"
                  name="pickupOption"
                  value={option}
                  checked={pickupOption === option}
                  onChange={(e) => setPickupOption(e.target.value)}
                />
                <span className="chip-label">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {/* PRIMARY ISSUE */}

        <div className="input-group-surgical">
          <label className="tech-label">Primary Issue</label>

          <select
            required
            value={issueOne}
            onChange={(e) => setIssueOne(e.target.value)}
          >
            <option value="">Select Issue</option>

            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {issueOne === "Other" && (
          <div className="input-group-surgical">
            <textarea
              placeholder="Describe Primary Issue..."
              value={otherIssueOne}
              onChange={(e) => setOtherIssueOne(e.target.value)}
            />
          </div>
        )}

        {/* SECONDARY ISSUE */}

        <div className="input-group-surgical">
          <label className="tech-label">Secondary Issue</label>

          <select
            value={issueTwo}
            onChange={(e) => setIssueTwo(e.target.value)}
          >
            <option value="">None</option>

            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        {issueTwo === "Other" && (
          <div className="input-group-surgical">
            <textarea
              placeholder="Describe Secondary Issue..."
              value={otherIssueTwo}
              onChange={(e) => setOtherIssueTwo(e.target.value)}
            />
          </div>
        )}

        {/* ADDRESS */}

        <div className="input-group-surgical">
          <label className="tech-label">
            {pickupOption === "Pickup & Drop"
              ? "Pickup Address"
              : "Landmark (Optional)"}
          </label>

          <button
            type="button"
            onClick={getLiveLocation}
            className="live-location-btn"
          >
            Use My Live Location
          </button>

          <textarea
            required={pickupOption === "Pickup & Drop"}
            placeholder="Enter full pickup address..."
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        {/* SUBMIT */}

        <button
          type="submit"
          className="book-now-btn-surgical"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : "Confirm Booking"}
        </button>

      </form>
    </div>
  );
};

export default Book;

